import { http } from "./http";
import type { Role } from "./auth.service";

/**
 * Contratos administrativos (/api/admin, /api/organizations, /api/users).
 * O escopo é decidido no backend a partir do usuário autenticado —
 * o frontend nunca envia organização/escopo para "ganhar" acesso.
 */

export interface AdminOverview {
  scope: {
    role: Role;
    role_label: string;
    organizations: number;
    can_see_revenue: boolean;
    can_manage_platform: boolean;
  };
  metrics: {
    mrr_cents: number | null;
    currency: string;
    masters: number;
    franqueados: number;
    clientes: number;
    users: number;
    projects: number;
    ai_runs_30d: number;
    ai_runs_failed_30d: number;
    usage_30d: number;
    deployments_30d: number;
    subscriptions_active: number;
    subscriptions_trial: number;
    providers_total: number;
    providers_healthy: number;
  };
  organizations: OrganizationNode[];
}

export interface OrganizationNode {
  id: string;
  name: string;
  tier: string;
  status: string;
  depth: number;
  projects_count: number;
  members_count: number;
  children: OrganizationNode[];
}

export interface AdminOrganization {
  id: string;
  name: string;
  slug: string;
  tier: string;
  status: string;
  document?: string | null;
  parent_id?: string | null;
  parent_name?: string | null;
  projects_count?: number;
  members_count?: number;
}

export interface OrganizationInput {
  name: string;
  tier: string;
  parent_id?: string | null;
  document?: string | null;
  status?: string;
}

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  role_label: string;
  is_active: boolean;
  last_login_at: string | null;
  organizations?: { id: string; name: string; tier: string }[];
}

export interface RoleOption {
  value: Role;
  label: string;
}

export interface PlatformSettings {
  platform_name: string | null;
  support_email: string | null;
  signup_enabled: boolean | null;
  default_plan_slug: string | null;
  default_project_limit: number | null;
  default_ai_run_limit: number | null;
  default_deploy_limit: number | null;
  default_storage_mb_limit: number | null;
  sandbox_max_containers: number | null;
}

export const adminService = {
  overview: () => http.get<AdminOverview>("/admin/overview"),

  organizations: () =>
    http.getWithMeta<AdminOrganization[], { assignable_tiers: string[] }>("/organizations"),
  createOrganization: (input: OrganizationInput) =>
    http.post<AdminOrganization>("/organizations", input),
  updateOrganization: (id: string, input: Partial<OrganizationInput>) =>
    http.put<AdminOrganization>(`/organizations/${id}`, input),
  deleteOrganization: (id: string) => http.delete<{ deleted: boolean }>(`/organizations/${id}`),

  users: (search?: string) =>
    http.getWithMeta<AdminUser[], { assignable_roles: RoleOption[] }>(
      `/users${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),
  createUser: (input: {
    name: string;
    email: string;
    password: string;
    role: Role;
    organization_id: string;
    is_active?: boolean;
  }) => http.post<AdminUser>("/users", input),
  updateUser: (
    id: string,
    input: Partial<{ name: string; email: string; password: string; role: Role; is_active: boolean }>,
  ) => http.put<AdminUser>(`/users/${id}`, input),
  deleteUser: (id: string) => http.delete<{ deleted: boolean }>(`/users/${id}`),

  settings: () => http.get<PlatformSettings>("/admin/settings"),
  updateSettings: (input: Partial<PlatformSettings>) =>
    http.put<PlatformSettings>("/admin/settings", input),
};
